import PropTypes from "prop-types";
import ButtonComponent from "../../atoms/button";
import { MinusIcon, PlusIcon } from "@phosphor-icons/react";

/**
 * AddItemButton component.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {number} props.selectedItem - The selected item.
 * @param {function} props.setSelectedItem - The function to set the selected item.
 * @param {function} props.onRemoveItem - The function to remove the item.
 * @param {string} props.parentStyle - The style for the parent container.
 * @param {boolean} props.isDisabled - Whether the button is disabled.
 * @param {boolean} props.isFromPDP - Whether the button is rendered on the PDP page.
 * @param {string} props.buttonVariant - The variant of the button.
 * @param {string} props.classes - Additional classes for the button.
 * @param {string} props.btnParentClasses - Additional classes for the button parent container.
 * @returns {JSX.Element} The AddItemButton component.
 *
 * This component renders a button that allows users to add or remove items from their selection. It displays the current count of selected items and provides controls to increment or decrement this count. The button's appearance and behavior can be customized based on the provided props, such as disabling the button or changing its variant.
 * @returns {JSX.Element} The AddItemButton component.
 */
const AddItemButton = ({
  selectedItem,
  setSelectedItem,
  onRemoveItem,
  parentStyle,
  isFromPDP = false,
  buttonVariant = "iconWithTextOutlined",
  classes = "",
  btnParentClasses = "",
  isMobile = false,
  isBottomDrawer = false,
  isAddOnDetails = false,
  isFromPopUp = false,
  isFromCheckout = false,
}) => {
  // Maximum number of items allowed
  const MAX_ITEM_COUNT = 9;
  /**
   * Handle the change in item count.
   *
   * @param {number} count - The new item count.
   */
  const onItemCountChange = (count) => {
    // Prevent setting a count greater than MAX_ITEM_COUNT
    if (count > MAX_ITEM_COUNT) return;
    setSelectedItem(count);
  };

  if (!selectedItem) {
    if (((isFromPDP && isMobile) || isFromCheckout) && !isBottomDrawer && !isAddOnDetails) {
      return (
        <div
          onClick={() => onItemCountChange(1)}
          className="flex h-[28px] w-[28px] cursor-pointer items-center justify-center rounded-8 border-1 border-lightOliveGreen bg-white-900"
        >
          <PlusIcon size={16} weight="bold" color="#737530" />
        </div>
      );
    }
    return (
      <ButtonComponent
        onClick={() => onItemCountChange(1)}
        buttonVariant={buttonVariant}
        classes={`text-fnp-300 border border-lightOliveGreen py-8 px-16 hover:bg-fnp-100 ease-in-out duration-300 ${isFromPDP ? "w-full mt-10" : ""} ${classes}`}
        label={"ADD"}
        size=""
        labelStyle="text-14 font-600"
        btnParentClasses={btnParentClasses}
      />
    );
  }

  /**
   * Handles the removal or decrement of the selected item count.
   * If the selected item count is 1, it triggers the removal of the item.
   * Otherwise, it decrements the item count by one.
   */
  const onHandleRemoveORsubtract = () => {
    if (selectedItem === 1) {
      return onRemoveItem(selectedItem);
    }
    return onItemCountChange(selectedItem - 1);
  };

  return (
    <div
      className={
        parentStyle ||
        `flex  bg-white-900 ${(isFromPDP && isMobile) || isFromCheckout ? "h-[28px] w-full" : "h-[34px] w-[85px]"} ${isBottomDrawer || isFromPopUp ? "mt-0" : "mt-10"} items-center justify-between gap-10 rounded-8 border-1 border-lightOliveGreen p-4 ${classes}`
      }
    >
      <div
        onClick={onHandleRemoveORsubtract}
        className={`flex cursor-pointer items-center justify-center rounded-[4px] bg-fnp-50 p-[2px] ${(isMobile && !isBottomDrawer) || isFromCheckout ? "h-[16px] w-[16px]" : "h-[20px] w-[20px]"}`}
      >
        <MinusIcon size={isMobile && !isBottomDrawer ? 12 : 20} color="#737530" weight="bold" />
      </div>
      <span className="text-14 font-600 text-fnp-300">{selectedItem}</span>
      <div
        onClick={() => {
          if (selectedItem >= MAX_ITEM_COUNT) return;
          onItemCountChange(selectedItem + 1);
        }}
        className={`${selectedItem >= MAX_ITEM_COUNT ? "cursor-not-allowed opacity-50" : "cursor-pointer"} ${(isMobile && !isBottomDrawer) || isFromCheckout ? "h-[16px] w-[16px]" : "h-[20px] w-[20px]"} flex items-center justify-center rounded-[4px] bg-fnp-50  p-[2px]`}
      >
        <PlusIcon size={(isMobile && !isBottomDrawer) || isFromCheckout ? 12 : 20} color="#737530" weight="bold" />
      </div>
    </div>
  );
};

AddItemButton.propTypes = {
  selectedItem: PropTypes.number.isRequired,
  setSelectedItem: PropTypes.func.isRequired,
  onRemoveItem: PropTypes.func.isRequired,
  parentStyle: PropTypes.string,
  isFromPDP: PropTypes.bool,
  buttonVariant: PropTypes.string,
  classes: PropTypes.string,
  btnParentClasses: PropTypes.string,
  isMobile: PropTypes.bool,
  isBottomDrawer: PropTypes.bool,
  isAddOnDetails: PropTypes.bool,
  isFromPopUp: PropTypes.bool,
  isFromCheckout: PropTypes.bool,
};

export default AddItemButton;
